export type ProductCategory = 'CAFTAN' | 'TAKCHITA' | 'DJELLABA' | 'OTHER';

// CUSTOM    : made-to-measure — buyer books a fitting appointment, no stock.
// READY_TO_WEAR : off-the-rack — sold in fixed sizes via cart/WhatsApp checkout.
export type ProductType = 'CUSTOM' | 'READY_TO_WEAR';

export interface ProductVariant {
  size: string;
  stock: number;
}

// Supported content languages for product copy. Mirrors the backend.
export type ContentLocale = 'ar' | 'fr' | 'en';

// One locale's worth of product copy. Either field may be absent for a
// partial translation; the resolver falls back to the source.
export interface LocalizedText {
  title?: string;
  description?: string;
}

export interface Product {
  id: string;
  title: string;
  price: number;
  originalPrice?: number;
  description: string;
  // Language of the canonical title/description above (what the seller
  // typed, usually Arabic). Defaults to 'fr' for legacy rows.
  sourceLocale?: string;
  // Per-locale translated copy keyed by locale code ('fr','en',...).
  // Excludes the source locale (that lives in title/description).
  translations?: Record<string, LocalizedText>;
  imageUrl: string;
  /**
   * The raw photo the AI imagery was generated from. Admin-only: the API
   * strips it for non-admin callers, so it is always undefined on the
   * storefront. Kept so a product can be re-run through the AI flow later.
   */
  sourceImageUrl?: string;
  images?: string[];
  rating: number;
  measurementDuration: string;
  category?: ProductCategory;
  productType?: ProductType;
  variants?: ProductVariant[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CartItem {
  productId: string;
  title: string;
  imageUrl: string;
  unitPrice: number;
  size: string;
  quantity: number;
}

export interface TimeSlot {
  id: string;
  label: string;
  time: string;
}

export type AppointmentStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';

export interface AppointmentInput {
  name: string;
  email: string;
  phone: string;
  appointmentDate: string;
  timeSlot: string;
  notes: string;
  productId: string;
  // Anti-bot. Hidden honeypot field — real users never fill it.
  // Bots that auto-fill every input populate it and get rejected server-side.
  website?: string;
  // Anti-bot. Epoch-millis when the booking modal opened. Server rejects
  // submissions faster than ~2 s — bots submit instantly, humans don't.
  formOpenedAt?: number;
}

export interface Appointment extends AppointmentInput {
  id: string;
  userId: string;
  status: AppointmentStatus;
  createdAt: string;
  updatedAt: string;
}

export type UserRole = 'ADMIN' | 'CUSTOMER';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

// ---- AI photo transformation ---------------------------------------------

export type AiTransformationStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'ACCEPTED'
  | 'REJECTED';

// Pose variants for the 4-image transformation set. Order is intentional
// (matches backend enum order) — drives the grid render order in the UI.
export type PoseVariant =
  | 'STANDING_FRONT'
  | 'SEATED'
  | 'BACK_OVER_SHOULDER'
  | 'WALKING_SIDE';

export const POSE_VARIANTS: readonly PoseVariant[] = [
  'STANDING_FRONT',
  'SEATED',
  'BACK_OVER_SHOULDER',
  'WALKING_SIDE',
] as const;

export interface AiTransformJob {
  id: string;
  productId?: string;
  // Null on legacy single-pose jobs (created via /api/admin/ai/transform).
  // Populated on every job spawned via /api/admin/ai/transform-set.
  setId?: string;
  poseVariant?: PoseVariant;
  status: AiTransformationStatus;
  originalImageUrl: string;
  transformedImageUrl?: string;
  errorMessage?: string;
  prompt: string;
  negativePrompt?: string;
  createdAt: string;
  completedAt?: string;
}

export interface AiTransformStartInput {
  originalImageUrl: string;
  prompt?: string;
  negativePrompt?: string;
  productId?: string;
}

export interface AiTransformSetStartInput {
  originalImageUrl: string;
  prompt?: string;
  productId?: string;
}

/**
 * Wire shape returned by POST /transform-set and GET /sets/{id}.
 * - `jobs` is the full history newest-first across all poses.
 * - `currentJobByPose` is the per-pose "latest attempt" map the front
 *   uses to render each grid cell.
 */
export interface AiTransformSet {
  id: string;
  productId?: string;
  originalImageUrl: string;
  createdAt: string;
  jobs: AiTransformJob[];
  currentJobByPose: Partial<Record<PoseVariant, AiTransformJob>>;
}

/**
 * Audience figures, as GoAccess writes them on the VPS.
 *
 * Deliberately loose: the API forwards GoAccess's JSON verbatim, and its
 * panels differ between versions and depending on which flags the ops job
 * runs with (the country panel, for instance, only exists once a GeoIP
 * database is installed). A strict type would compile fine and then render
 * nothing when a panel moved. Consumers read what is present and say so when
 * a panel is missing.
 *
 * `hits.count` is per request, `visitors.count` per unique visitor — the two
 * differ by roughly 2x in practice and must not be swapped.
 */
export interface AudiencePanelRow {
  data: string;
  hits?: { count?: number };
  visitors?: { count?: number };
  /** Country rows nested under a continent row. */
  items?: AudiencePanelRow[];
}

export interface AudiencePanel {
  data?: AudiencePanelRow[];
}

export interface AudienceReport {
  general?: {
    unique_visitors?: number;
    total_requests?: number;
    start_date?: string;
    end_date?: string;
  };
  /** Per-day series — the row label is the date. */
  visitors?: AudiencePanel;
  requests?: AudiencePanel;
  referring_sites?: AudiencePanel;
  geolocation?: AudiencePanel;
  [panel: string]: unknown;
}
